import type { Tag } from "./types";

/** Option row for Notifications (and future) linked-tag dropdowns. */
export interface TagLinkSelectOption {
  id: string;
  name: string;
  /** Unique dropdown label (may include owning customer for disambiguation). */
  label: string;
  tenantName?: string;
  childTag?: boolean;
}

/**
 * Filters the Tags catalog to tags linkable at the current SideNav lens.
 * Mirrors Tags Manager §3.9 picker `scope=self` semantics:
 * - Partner / GA partner-view → partner-owned tags only (`childTag !== true`)
 * - Customer / drilled customer view → acting-tenant tags only
 *
 * Keeps the tag already linked on edit so hydration does not blank the dropdown
 * when a child-owned tag was linked before a lens change.
 */
export function filterTagsForNotificationLink(
  tags: Tag[],
  linkedTagId?: string | null
): Tag[] {
  return tags.filter(
    (t) => t.childTag !== true || (linkedTagId != null && t.id === linkedTagId)
  );
}

function disambiguateLabel(base: string, used: Map<string, string>, id: string): string {
  let label = base;
  let suffix = 2;
  while (used.has(label) && used.get(label) !== id) {
    label = `${base} (${suffix})`;
    suffix += 1;
  }
  used.set(label, id);
  return label;
}

/**
 * Builds sorted linked-tag dropdown options from filtered catalog rows.
 * When `includeTenantInLabel` is true (partner / GA all-customers lens), appends
 * ` — CustomerName` for child-owned tags — same disambiguation as Tags Manager.
 */
export function buildTagLinkSelectOptions(
  tags: Tag[],
  options?: { includeTenantInLabel?: boolean }
): TagLinkSelectOption[] {
  const usedLabels = new Map<string, string>();
  const rows = tags.map((t) => {
    const showTenant =
      options?.includeTenantInLabel === true &&
      t.childTag === true &&
      !!t.tenant?.name;
    const baseLabel =
      showTenant && t.tenant?.name ? `${t.name} — ${t.tenant.name}` : t.name;
    const label = disambiguateLabel(baseLabel, usedLabels, t.id);
    return {
      id: t.id,
      name: t.name,
      label,
      tenantName: t.tenant?.name,
      childTag: t.childTag,
    };
  });

  return rows.sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
  );
}

/** Maps dropdown label → tag id (labels are unique within the returned set). */
export function tagLinkLabelToIdMap(
  options: TagLinkSelectOption[]
): Map<string, string> {
  const map = new Map<string, string>();
  for (const opt of options) {
    map.set(opt.label, opt.id);
  }
  return map;
}
